import { Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { TranslationService } from '../../../core/i18n/translation.service';
import { DashboardLocationAudit, DashboardLocationAuditPage } from '../models/dashboard.models';
import { DashboardApi } from '../services/dashboard.api';

@Component({
  selector: 'app-dashboard-location-audits',
  templateUrl: './dashboard-location-audits.component.html',
  styleUrl: './dashboard-location-audits.component.css',
})
export class DashboardLocationAuditsComponent {
  protected readonly i18n = inject(TranslationService);
  private readonly api = inject(DashboardApi);
  private readonly destroyRef = inject(DestroyRef);
  readonly initial = input.required<DashboardLocationAuditPage>();
  readonly location = input('');
  readonly mine = input(false);
  readonly showRole = input(false);
  readonly pendingPlan = input<string | null>(null);
  readonly execute = output<DashboardLocationAudit>();
  readonly preview = output<string>();
  protected readonly data = signal<DashboardLocationAuditPage | null>(null);
  protected readonly page = signal(1);
  protected readonly loading = signal(false);
  protected readonly error = signal(false);
  protected readonly pages = computed(() => Math.max(1, Math.ceil((this.data()?.count ?? 0) / (this.data()?.page_size ?? 10))));
  private requestId = 0;
  private readonly now = signal(Date.now());
  private clockOffset = 0;

  constructor() {
    effect(() => {
      const initial = this.initial();
      ++this.requestId;
      this.clockOffset = initial.as_of ? Date.parse(initial.as_of) - Date.now() : 0;
      this.now.set(Date.now() + this.clockOffset);
      this.data.set(initial); this.page.set(initial.page); this.error.set(false); this.loading.set(false);
    });
    const clock = setInterval(() => this.now.set(Date.now() + this.clockOffset), 250);
    this.destroyRef.onDestroy(() => { ++this.requestId; clearInterval(clock); });
  }

  protected async load(page: number): Promise<void> {
    const request = ++this.requestId;
    this.page.set(page); this.loading.set(true); this.error.set(false);
    try {
      const data = await firstValueFrom(this.api.locationAudits(page, this.location(), this.mine()).pipe(takeUntilDestroyed(this.destroyRef)));
      if (request === this.requestId) {
        this.clockOffset = data.as_of ? Date.parse(data.as_of) - Date.now() : 0;
        this.data.set(data);
      }
    } catch {
      if (request === this.requestId) this.error.set(true);
    } finally {
      if (request === this.requestId) this.loading.set(false);
    }
  }

  protected action(row: DashboardLocationAudit): DashboardLocationAudit['action'] {
    if (row.planning_mode === 'annual' || !row.window_start_at || !row.window_end_at) return row.action;
    if (!row.can_execute || !row.plan_id || !['planned', 'in_progress'].includes(row.next_status ?? '')) return null;
    const now = this.now();
    return now >= Date.parse(row.window_start_at) && now <= Date.parse(row.window_end_at)
      ? row.next_status === 'in_progress' ? 'continue' : 'start' : null;
  }

  protected executeRow(row: DashboardLocationAudit): void {
    const action = this.action(row);
    if (action) this.execute.emit({ ...row, action });
  }

  protected windowLabel(row: DashboardLocationAudit): string {
    if (!row.window_start_at || !row.window_end_at) return '';
    const zone = row.time_zone ?? 'Europe/Warsaw';
    const format = new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', {
      timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    return `${format.format(new Date(row.window_start_at))}–${format.format(new Date(row.window_end_at))} (${zone})`;
  }

  protected windowClosed(row: DashboardLocationAudit): boolean {
    return !!row.window_end_at && this.now() > Date.parse(row.window_end_at);
  }

  protected date(value: string): string {
    return new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    }).format(new Date(value.length === 10 ? `${value}T12:00:00` : value));
  }

}
